import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, ScrollText } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { saveBlob } from "../../lib/files";
import { formatDateTime } from "../../lib/format";

const FILTERS = [["", "Todo"], ["repartir", "Repartir"], ["fusionar", "Fusionar"], ["eliminar", "Eliminar"]];
const PAGE = 100;

// REQ-317: Admin-only, read-only client audit log with Excel download
export const AuditLogDialog = () => {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setData(null); setError("");
    api.get("/client-audit", { params: { action: action || undefined, page, page_size: PAGE } })
      .then((r) => setData(r.data)).catch((e) => setError(errorMessage(e, "No se pudo cargar el registro")));
  }, [action, page]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/client-audit/excel", { params: { action: action || undefined }, responseType: "blob", timeout: 120000 });
      saveBlob(r.data, `registro_clientes_${action || "todo"}.xlsx`);
    } catch (e) {
      setError(errorMessage(e, "No se pudo descargar el registro"));
    } finally {
      setBusy(false);
    }
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="tr-btn-light w-fit px-3 py-2 text-xs" data-testid="audit-log-button">
        <ScrollText className="h-3.5 w-3.5" /> Registro de cambios de clientes
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="audit-log-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg font-extrabold text-[#182235]">Registro de cambios de clientes</DialogTitle>
            <DialogDescription className="text-xs text-[#5b667a]">Repartir, fusionar y eliminar clientes, con cada archivo y su ruta original. Solo lectura.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map(([k, label]) => (
              <button key={k || "todo"} type="button" onClick={() => { setAction(k); setPage(1); }}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${action === k ? "bg-[#0f5ef7] text-white" : "bg-[#eef3fb] text-[#182235]"}`}
                data-testid={`audit-filter-${k || "todo"}`}>{label}</button>
            ))}
            <button type="button" onClick={download} disabled={busy} className="tr-btn-light ml-auto px-3 py-1.5 text-xs" data-testid="audit-log-download">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Descargar (Excel)
            </button>
          </div>
          {error && <p role="alert" className="text-sm font-semibold text-[#b42318]" data-testid="audit-log-error">{error}</p>}
          {!data && !error && <p className="flex items-center gap-2 text-sm text-[#5b667a]"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>}
          {data && (
            <>
              <p className="text-xs text-[#5b667a]" data-testid="audit-log-total">{data.total} registros</p>
              <ul className="space-y-1 text-xs">
                {data.items.map((r, i) => (
                  <li key={i} className="rounded-lg bg-[#f5f8fd] px-2.5 py-1.5" data-testid={`audit-log-item-${i}`}>
                    <span className="font-semibold text-[#182235]">{formatDateTime(r.at)}</span> · {r.label} · {r.name} <span className="font-mono text-[#5b667a]">{r.borrower_id}</span> · {r.by}
                    {r.file && <> · <span className="break-all font-mono text-[#5b667a]">{r.path || r.file}</span>{r.to && <> → <span className="font-mono">{r.to}</span></>}{r.result && <> · {r.result}</>}</>}
                    {r.detail && <> · {r.detail}</>}
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-end gap-2 text-xs">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="tr-btn-light px-2.5 py-1" data-testid="audit-log-prev">Anterior</button>
                <span data-testid="audit-log-page">{page} / {pages}</span>
                <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className="tr-btn-light px-2.5 py-1" data-testid="audit-log-next">Siguiente</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
